/**
 * App.tsx - Phase 3H + Beta 3 War Room + Phase 4 Head-to-Head
 *
 * Main application component with routing, auth protection, and providers.
 * 
 * CHANGES FOR WAR ROOM:
 * - Line 24: Added WarRoomPage import
 * - Line 91: Replaced DashboardPage with WarRoomPage at path "/"
 * 
 * ROUTING FIX (Architect Directive):
 * - /pools/:poolId/leaderboard now REDIRECTS to /pools/:poolId/gm?view=leaderboard
 * - This ensures legacy URLs land in GM hub leaderboard view
 * 
 * PHASE 4 ADDITION:
 * - Added HeadToHeadPage import
 * - Added route /standings/:poolId/vs/:opponentUserId for head-to-head scoreboard
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppLayout } from './components/layout/AppLayout';
import { AdminRoute } from './routes/AdminRoute';

// =============================================================================
// Page Imports - Direct imports (stable)
// =============================================================================

// Auth Pages (Phase 3H)
import { LoginPage } from './pages/LoginPage';

// Player Pages
import { WarRoomPage } from './pages/WarRoomPage';  // <-- NEW: War Room replaces Dashboard
import { DashboardPage } from './pages/DashboardPage';  // Keep for potential legacy access
import { PoolsPage } from './pages/PoolsPage';
import { PoolDetailPage } from './pages/PoolDetailPage';
import { PickFlowPage } from './pages/PickFlowPage';
import { GMFlowPage } from './pages/GMFlowPage';
import GMStrategyHub from './pages/GMStrategyHub';
import { TunnelPage } from './pages/TunnelPage';
import { FieldReportPage } from './pages/FieldReportPage';
import { HeadToHeadPage } from './pages/HeadToHeadPage';  // <-- PHASE 4: Head-to-Head Scoreboard
// STANDINGS V11 - Direct render (redirect was causing blank screens)
import { ScoreboardScreen } from './features/scoreboard';

// Admin Pages
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminPoolsPage } from './pages/admin/AdminPoolsPage';
import { AdminMembershipsPage } from './pages/admin/AdminMembershipsPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { AdminBehaviorPage } from './pages/admin/AdminBehaviorPage';

// =============================================================================
// Legacy URL Redirect Component
// =============================================================================


// =============================================================================
// Placeholder Components
// =============================================================================

const AccountPage = () => <PlaceholderPage title="Account" phase="2C-2" />;
const AdminPoolDetailPage = () => <PlaceholderPage title="Admin - Pool Details" phase="2C-3" admin />;
const AdminPoolBehaviorPage = () => <PlaceholderPage title="Admin - Pool Behavior" phase="2C-4" admin />;

// =============================================================================
// Protected Route Component
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// App Routes (Inside AuthProvider)
// =============================================================================

function AppRoutes(): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* =========================== */}
      {/* Public Routes */}
      {/* =========================== */}
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} 
      />

      {/* =========================== */}
      {/* Protected Player Routes */}
      {/* =========================== */}
      
      {/* WAR ROOM - Homepage (Beta 3) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WarRoomPage />  {/* <-- CHANGED: Was DashboardPage */}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Legacy Dashboard - accessible via direct route if needed */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/pools"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PoolsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pools/:poolId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PoolDetailPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pools/:poolId/tunnel"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TunnelPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pools/:poolId/pick"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PickFlowPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pools/:poolId/gm"
        element={
          <ProtectedRoute>
            <AppLayout>
              <GMFlowPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      {/* SCOREBOARD V9 - Direct render */}
      <Route
        path="/pools/:poolId/leaderboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ScoreboardScreen />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      {/* FIELD REPORT - Beta 3 */}
      <Route
        path="/pools/:poolId/field-report"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FieldReportPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      {/* HEAD-TO-HEAD SCOREBOARD - Phase 4 */}
      <Route
        path="/standings/:poolId/vs/:opponentUserId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HeadToHeadPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      
      <Route
        path="/gm"
        element={
          <ProtectedRoute>
            <AppLayout>
              <GMStrategyHub />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AccountPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* =========================== */}
      {/* Protected Admin Routes */}
      {/* =========================== */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pools"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminPoolsPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pools/:poolId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminPoolDetailPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminMembershipsPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminAuditPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/behavior"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminBehaviorPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/behavior/:poolId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminRoute>
                <AdminPoolBehaviorPage />
              </AdminRoute>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* =========================== */}
      {/* Catch-all */}
      {/* =========================== */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// =============================================================================
// App Component
// =============================================================================

function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

// =============================================================================
// Placeholder Component
// =============================================================================

interface PlaceholderPageProps {
  title: string;
  phase?: string;
  admin?: boolean;
}

function PlaceholderPage({ title, phase, admin }: PlaceholderPageProps): JSX.Element {
  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-slate-800
                        flex items-center justify-center">
          <svg
            className="w-8 h-8 text-slate-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-slate-100 mb-2">{title}</h1>

        {admin && (
          <span className="inline-block mb-4 px-2 py-1 text-xs font-medium
                           bg-purple-600/20 text-purple-400 rounded">
            Admin Only
          </span>
        )}

        <p className="text-slate-400 mb-4">
          This page is under construction and will be available soon.
        </p>

        {phase && (
          <p className="text-sm text-slate-500">
            Scheduled for: <span className="text-slate-400">Phase {phase}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// 404 Not Found Component
// =============================================================================

function NotFound(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <div className="text-center max-w-md">
        <div className="text-8xl font-bold text-slate-700 mb-4">404</div>
        <h1 className="text-2xl font-bold text-slate-100 mb-2">Page Not Found</h1>
        <p className="text-slate-400 mb-6">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a
          href="/"
          className="inline-flex items-center justify-center px-4 py-2
                     text-sm font-medium text-white bg-blue-600 rounded-lg
                     hover:bg-blue-700 transition-colors"
        >
          Return to War Room
        </a>
      </div>
    </div>
  );
}

export default App;
